import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { ChangelogsService } from '../../services'
import { 
  FileText, 
  Calendar, 
  Plus, 
  Minus, 
  Edit, 
  GitBranch,
  Search,
  Filter
} from 'lucide-react'
import { Changelog } from '../../types'
import toast from 'react-hot-toast'

export default function ChangelogView() {
  const { usuario } = useAuth()
  const [changelogs, setChangelogs] = useState<Changelog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    versao: 'all',
    tipo: 'all'
  })

  useEffect(() => {
    loadChangelogs()
  }, [])

  const loadChangelogs = async () => {
    try {
      setIsLoading(true)
      const response = await ChangelogsService.getChangelogs()
      setChangelogs(response)
    } catch (error) {
      console.error('Erro ao carregar changelogs:', error)
      toast.error('Erro ao carregar changelogs')
    } finally {
      setIsLoading(false)
    }
  }



  const filteredChangelogs = changelogs.filter(changelog => {
    const matchesSearch = changelog.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         changelog.versao?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesVersao = filters.versao === 'all' || 
                          changelog.versao === filters.versao
    
    const matchesTipo = filters.tipo === 'all' || 
                       changelog.tipo_mudanca === filters.tipo
    
    return matchesSearch && matchesVersao && matchesTipo
  })

  // Agrupar changelogs por data
  const groupedChangelogs = filteredChangelogs.reduce((groups, changelog) => {
    const date = changelog.data_publicacao || 'Sem data'
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(changelog)
    return groups
  }, {} as Record<string, Changelog[]>)

  // Ordenar datas (mais recente primeiro)
  const sortedDates = Object.keys(groupedChangelogs).sort((a, b) => {
    if (a === 'Sem data') return 1
    if (b === 'Sem data') return -1
    return new Date(b).getTime() - new Date(a).getTime()
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Changelog
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Histórico de mudanças e atualizações do sistema
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {usuario?.nivel === 'dev' && (
              <button 
                onClick={() => window.location.href = '/changelogs/gerenciar'}
                className="btn btn-primary"
              >
                <Edit className="w-4 h-4 mr-2" />
                Gerenciar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Buscar changelog..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500"
            />
          </div>

          {/* Versão Filter */}
          <select
            value={filters.versao}
            onChange={(e) => setFilters({...filters, versao: e.target.value})}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500"
          >
            <option value="all">Todas as versões</option>
            {changelogs.map(changelog => (
              <option key={changelog.versao} value={changelog.versao}>
                {changelog.versao}
              </option>
            )).filter((option, index, self) => 
              self.findIndex(o => (o as unknown as HTMLOptionElement).value === (option as unknown as HTMLOptionElement).value) === index
            )}
          </select>

          {/* Tipo Filter */}
          <select
            value={filters.tipo}
            onChange={(e) => setFilters({...filters, tipo: e.target.value})}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500"
          >
            <option value="all">Todos os tipos</option>
            <option value="adicionado">Adicionado</option>
            <option value="alterado">Alterado</option>
            <option value="removido">Removido</option>
            <option value="corrigido">Corrigido</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
              <GitBranch className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total de Versões</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {new Set(changelogs.map(changelog => changelog.versao)).size}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
              <FileText className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total de Entradas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{changelogs.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400">
              <Calendar className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Este Mês</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {changelogs.filter(changelog => {
                  const thisMonth = new Date().getMonth()
                  const thisYear = new Date().getFullYear()
                  const changelogDate = new Date(changelog.data_publicacao || '')
                  return changelogDate.getMonth() === thisMonth && changelogDate.getFullYear() === thisYear
                }).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
              <Filter className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Filtrados</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{filteredChangelogs.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Changelog Timeline */}
      <div className="changelog-timeline">
        {sortedDates.map((date) => (
          <div key={date} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
            <div className="bg-gradient-to-r from-primary-50 dark:from-primary-900/30 to-blue-50 dark:to-blue-900/30 px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center gap-3">
                <Calendar className="text-primary-600 dark:text-primary-400" size={24} />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {date === 'Sem data' ? 'Sem data definida' : new Date(date).toLocaleDateString('pt-BR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h3>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                {/* Adicionados */}
                {groupedChangelogs[date].filter(c => c.tipo_mudanca === 'adicionado').length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-green-700 dark:text-green-400 mb-4 flex items-center gap-2">
                      <Plus size={20} />
                      Adicionado
                    </h4>
                    <div className="space-y-3">
                      {groupedChangelogs[date].filter(c => c.tipo_mudanca === 'adicionado').map((changelog) => (
                        <div key={changelog.id} className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/30 rounded-xl border border-green-200 dark:border-green-700 changelog-item">
                          <div className="w-3 h-3 bg-green-500 dark:bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1">
                            <div className="mb-2">
                              <div className="flex items-center gap-2 mb-2">
                                {changelog.versao && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                                    v{changelog.versao}
                                  </span>
                                )}
                                <span className="text-sm text-gray-500 dark:text-gray-400">•</span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-300 font-medium whitespace-pre-line">{changelog.descricao}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Alterados */}
                {groupedChangelogs[date].filter(c => c.tipo_mudanca === 'alterado').length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-yellow-700 dark:text-yellow-400 mb-4 flex items-center gap-2">
                      <Edit size={20} />
                      Alterado
                    </h4>
                    <div className="space-y-3">
                      {groupedChangelogs[date].filter(c => c.tipo_mudanca === 'alterado').map((changelog) => (
                        <div key={changelog.id} className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-xl border border-yellow-200 dark:border-yellow-700 changelog-item">
                          <div className="w-3 h-3 bg-yellow-500 dark:bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1">
                            <div className="mb-2">
                              <div className="flex items-center gap-2 mb-2">
                                {changelog.versao && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300">
                                    v{changelog.versao}
                                  </span>
                                )}
                                <span className="text-sm text-gray-500 dark:text-gray-400">•</span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-300 font-medium whitespace-pre-line">{changelog.descricao}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Corrigidos */}
                {groupedChangelogs[date].filter(c => c.tipo_mudanca === 'corrigido').length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-blue-700 dark:text-blue-400 mb-4 flex items-center gap-2">
                      <Edit size={20} />
                      Corrigido
                    </h4>
                    <div className="space-y-3">
                      {groupedChangelogs[date].filter(c => c.tipo_mudanca === 'corrigido').map((changelog) => (
                        <div key={changelog.id} className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-700 changelog-item">
                          <div className="w-3 h-3 bg-blue-500 dark:bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1">
                            <div className="mb-2">
                              <div className="flex items-center gap-2 mb-2">
                                {changelog.versao && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                                    v{changelog.versao}
                                  </span>
                                )}
                                <span className="text-sm text-gray-500 dark:text-gray-400">•</span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-300 font-medium whitespace-pre-line">{changelog.descricao}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Removidos */}
                {groupedChangelogs[date].filter(c => c.tipo_mudanca === 'removido').length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-4 flex items-center gap-2">
                      <Minus size={20} />
                      Removido
                    </h4>
                    <div className="space-y-3">
                      {groupedChangelogs[date].filter(c => c.tipo_mudanca === 'removido').map((changelog) => (
                        <div key={changelog.id} className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/30 rounded-xl border border-red-200 dark:border-red-700 changelog-item">
                          <div className="w-3 h-3 bg-red-500 dark:bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1">
                            <div className="mb-2">
                              <div className="flex items-center gap-2 mb-2">
                                {changelog.versao && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300">
                                    v{changelog.versao}
                                  </span>
                                )}
                                <span className="text-sm text-gray-500 dark:text-gray-400">•</span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-300 font-medium whitespace-pre-line">{changelog.descricao}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {filteredChangelogs.length === 0 && (
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-12 border border-gray-200/50 dark:border-gray-700/50 shadow-soft text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Nenhum changelog encontrado
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Tente ajustar os filtros de busca ou não há registros de mudanças no sistema.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
